import React, { useState, useContext, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  Box,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
// import { DatePicker } from '@mui/x-date-pickers';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Grid from '@mui/material/Unstable_Grid2';
import { toast } from 'react-toastify';
// Helpers
import moment from 'moment';
import { filterRepOptions } from '../../../helpers/filterFunctions';
// API
import { getRepFilter } from '../../../api/filterOptionsApi';
import { getProductFilter } from '../../../api/dropdownOptionsApi';
import { postProductInventoryBalanceList } from '../../../api/productAllocationApi';
import useProductAllocationContext from '../context/useProductAllocationContext';

export default function ProductInventoryBalanceModal() {
  const { productInventoryBalanceModal, setProductInventoryModal } = useContext(
    useProductAllocationContext,
  );

  const filterFormDefault = {
    rep_id: {
      label: productInventoryBalanceModal.rep_name,
      value: productInventoryBalanceModal.rep_id,
    },
    product: { label: 'ALL', value: null },
    from: moment().format('YYYY-MM-01'),
    to: moment().format('YYYY-MM-DD'),
  };

  const [sortModel, setSortModel] = useState([
    {
      field: 'id',
      sort: 'desc',
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [inventoryBalance, setInventoryBalance] = useState([]);
  const [filterForm, setFilterForm] = useState(filterFormDefault);
  const [repOptions, setRepOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);

  const handleCancel = () => {
    setProductInventoryModal({
      ...productInventoryBalanceModal,
      toggle: false,
      rep_id: null,
      rep_name: null,
    });
  };

  const loadRepDropdown = async () => {
    const request = {
      date_from: filterForm?.from,
      date_to: filterForm?.to,
    };
    const reponse = await getRepFilter(request);
    const { code, data } = reponse.data;
    if (code === '200') {
      const dropdownValue = filterRepOptions(data);
      setRepOptions([{ label: 'ALL', value: null }, ...dropdownValue]);
    }
  };

  const loadProductDropdown = async () => {
    const request = {
      active: 1,
    };
    const reponse = await getProductFilter(request);
    const { code, data } = reponse.data;
    if (code === '200') {
      const dropdownValue = data
        ?.filter((row) => row.product_name !== '')
        .map((row) => ({
          value: row.id,
          label: row.product_name,
        }));

      setProductOptions([{ label: 'ALL', value: null }, ...dropdownValue]);
    }
  };

  const handleChangeFilter = (e, row, name) => {
    setFilterForm({ ...filterForm, [name]: row });
  };

  const handleGenerateFilter = async (clear = null) => {
    try {
      setLoading(true);
      let request;
      if (clear === 'reset') {
        request = {
          rep_id: filterFormDefault?.rep_id?.value,
          product: filterFormDefault?.product?.value,
        };
      } else {
        request = {
          rep_id: filterForm?.rep_id?.value,
          product: filterForm?.product?.value,
        };
      }

      const response = await postProductInventoryBalanceList(request);
      const { code, data } = response.data;

      if (code === '200') {
        setInventoryBalance(data);
      }
      setLoading(false);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleClearFilter = () => {
    setFilterForm(filterFormDefault);
    handleGenerateFilter('reset');
  };

  const columns = [
    {
      field: 'md_name',
      headerName: 'MD Name',
      width: 200,
      flex: 1,
      cellClassName: ({ row }) => `cell-row-${row.id}`,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'product_name',
      headerName: 'Product Name',
      width: 200,
      flex: 1,
      cellClassName: ({ row }) => `cell-row-${row.id}`,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'month_format',
      headerName: 'Month',
      width: 200,
      flex: 1,
      cellClassName: ({ row }) => `cell-row-${row.id}`,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'beg_balance',
      headerName: 'Beginning Balance',
      width: 200,
      flex: 1,
      cellClassName: ({ row }) => `cell-row-${row.id}`,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'allocation',
      headerName: 'Allocation',
      width: 200,
      flex: 1,
      cellClassName: ({ row }) => `cell-row-${row.id}`,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'dispense',
      headerName: 'Distribution',
      width: 200,
      flex: 1,
      cellClassName: ({ row }) => `cell-row-${row.id}`,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'balance',
      headerName: 'Remaining Balance',
      width: 200,
      flex: 1,
      cellClassName: ({ row }) => `cell-row-${row.id}`,
      headerClassName: 'super-app-theme--header',
    },
  ];

  const pinnedRows = {
    top: inventoryBalance.length ? [inventoryBalance[0]] : [],
  };

  const getRemainingBalance = () => {
    const sum = inventoryBalance.reduce((prev, current) => prev + +current.balance, 0);

    return sum;
  };
  useEffect(() => {
    loadRepDropdown();
    loadProductDropdown();
    handleGenerateFilter();
  }, []);

  return (
    <div>
      <Dialog
        open={productInventoryBalanceModal?.toggle}
        onClose={handleCancel}
        maxWidth='xl'
        fullWidth
      >
        <DialogTitle>
          Product Sample Inventory Balance Per MPR{' '}
          <IconButton
            aria-label='close'
            onClick={handleCancel}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid md={2}>
              <Autocomplete
                disablePortal
                id='rep_id'
                disableClearable
                fullWidth
                options={repOptions}
                // eslint-disable-next-line react/jsx-props-no-spreading
                renderInput={(params) => <TextField {...params} label='MPR' />}
                size='small'
                value={filterForm?.rep_id}
                onChange={(e, newValue) => handleChangeFilter(e, newValue, 'rep_id')}
                sx={{ backgroundColor: '#fff' }}
              />
            </Grid>
            <Grid md={2}>
              <Autocomplete
                disablePortal
                id='product'
                disableClearable
                fullWidth
                options={productOptions}
                // eslint-disable-next-line react/jsx-props-no-spreading
                renderInput={(params) => <TextField {...params} label='Product' />}
                size='small'
                value={filterForm?.product}
                onChange={(e, newValue) => handleChangeFilter(e, newValue, 'product')}
                sx={{ backgroundColor: '#fff' }}
              />
            </Grid>

            <Grid md={3}>
              <Button variant='contained' color='success' onClick={handleGenerateFilter}>
                Generate
              </Button>
              <Button
                variant='contained'
                color='primary'
                onClick={() => handleClearFilter()}
                sx={{ marginLeft: '10px' }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid md={12} sx={{}}>
              <Box sx={{ float: 'right' }}>
                <Box sx={{ fontWeight: 'bold', color: 'red' }}>
                  {' '}
                  Current Remaining Balance: {getRemainingBalance()}
                </Box>

                <Box sx={{ color: 'red' }}> As of Today: {moment().format('YYYY-MM-DD hh:mm')}</Box>
              </Box>
            </Grid>
            <Grid md={12}>
              <Box
                className='table-container'
                sx={{
                  height: '600px',
                  marginLeft: '20px',
                  '& .super-app-theme--header': {
                    fontWeight: 'bold',
                    backgroundColor: '#070738',
                    color: '#fff',
                  },
                  '& .MuiDataGrid-pinnedRows': { fontWeight: 'bold', backgroundColor: '#FBCEB1' },
                }}
              >
                <DataGridPro
                  loading={loading}
                  rows={inventoryBalance}
                  columns={columns}
                  sortModel={sortModel}
                  pinnedRows={pinnedRows}
                  experimentalFeatures={{ rowPinning: true }}
                  initialState={{
                    ...inventoryBalance.initialState,
                    pagination: { paginationModel: { pageSize: 50 } },
                    // pinnedColumns: { left: ['head_name', 'manager_name', 'rep_name'], right: [] },
                  }}
                  pageSizeOptions={[50, 75, 100]}
                  pagination
                  onSortModelChange={(model) => setSortModel(model)}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant='contained' color='error' onClick={handleCancel}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
